import { sanitizeRichHtml } from './richText';

const CLIPBOARD_KEY = 'stickyhq_clipboard_history_v1';
const RECENTS_KEY = 'stickyhq_recent_notes_v1';
const MAX_CLIPBOARD_ITEMS = 40;
const MAX_RECENT_NOTES = 30;

const emit = (name, detail) => {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(name, { detail }));
};

const safeParse = (raw, fallback = []) => {
  try {
    const parsed = JSON.parse(raw || '');
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export function getClipboardHistory() {
  if (typeof localStorage === 'undefined') return [];
  return safeParse(localStorage.getItem(CLIPBOARD_KEY));
}

export function rememberClipboard(text, options = {}) {
  const value = String(text || '').trim();
  if (!value) return getClipboardHistory();
  const history = getClipboardHistory();
  const existing = history.filter(item => item.text !== value);
  const next = [{
    id: `clip-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    text: value.slice(0, 12000),
    source: options.source || 'clipboard',
    createdAt: Date.now()
  }, ...existing].slice(0, MAX_CLIPBOARD_ITEMS);
  localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(next));
  emit('stickyhq:clipboard-history-changed', next);
  return next;
}

export function clearClipboardHistory() {
  localStorage.removeItem(CLIPBOARD_KEY);
  emit('stickyhq:clipboard-history-changed', []);
}

export function recordRecentNote(noteId) {
  if (!noteId || typeof localStorage === 'undefined') return [];
  const current = safeParse(localStorage.getItem(RECENTS_KEY));
  const next = [String(noteId), ...current.filter(id => id !== String(noteId))].slice(0, MAX_RECENT_NOTES);
  localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  emit('stickyhq:recent-notes-changed', next);
  return next;
}

export function getRecentNoteIds() {
  if (typeof localStorage === 'undefined') return [];
  return safeParse(localStorage.getItem(RECENTS_KEY));
}

export function noteToPlainText(note) {
  if (!note) return '';
  const lines = [];
  if (note.title) lines.push(note.title);
  if (note.type === 'checklist') {
    (note.checklistItems || []).forEach(item => lines.push(`${item.done ? '☑' : '☐'} ${item.text || ''}`));
  } else if (note.type === 'table') {
    const columns = note.table?.columns || [];
    if (columns.length) lines.push(columns.map(c => c.name || '').join('\t'));
    (note.table?.rows || []).forEach(row => lines.push(columns.map(c => row.cells?.[c.id] || '').join('\t')));
  } else if (note.bodyText) {
    lines.push(note.bodyText);
  }
  return lines.filter(Boolean).join('\n\n').trim();
}

export async function copyText(text, source = 'copy') {
  const value = String(text || '');
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    rememberClipboard(value, { source });
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    textarea.remove();
    if (ok) rememberClipboard(value, { source });
    return ok;
  }
}

export async function copyNote(note, mode = 'rich') {
  const plain = noteToPlainText(note);
  if (!plain) return false;
  if (mode === 'plain' || !note?.bodyHtml || typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
    return copyText(plain, 'note-copy');
  }

  const titleHtml = note.title ? `<h2>${String(note.title).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</h2>` : '';
  const html = `${titleHtml}${sanitizeRichHtml(note.bodyHtml)}`;
  try {
    const item = new ClipboardItem({
      'text/plain': new Blob([plain], { type: 'text/plain' }),
      'text/html': new Blob([html], { type: 'text/html' })
    });
    await navigator.clipboard.write([item]);
    rememberClipboard(plain, { source: 'note-copy' });
    return true;
  } catch {
    return copyText(plain, 'note-copy');
  }
}